import React, { useEffect } from "react";

import DropdownList from "shared/components/inputs/dropdown/DropdownList";
import { DropdownWrapper } from "shared/components/inputs/dropdown/Dropdown.styles";
import { FEEDBACK_TYPES } from "shared/config/feedbackTypes";
import PropTypes from "prop-types";
import TextInput from "shared/components/inputs/input/TextInput";
import { useCombobox } from "downshift";
import useDropdownListOptions from "shared/components/inputs/dropdown/useDropdownListOptions";

const TextAutocomplete = React.forwardRef(
    (
        {
            items = [],
            value,
            placeholder = "Select",
            onChange = () => void 0,
            itemsAreLoading = false,
            disabled = false,
            feedbackType,
            clearDataOnUnmount = false,
            disabledOptionsList = [],
            position
        },
        ref
    ) => {
        const {
            filteredOptions,
            filterValue,
            setFilterValue,
            internalFilterUIShown,
        } = useDropdownListOptions(items, {
            usesExternalFilterUI: true,
            unfilteredResultListShowsEmpty: true,
        });

        const selectedItem = items.find((item) => item.value === value) ?? null;

        useEffect(() => {
            if (clearDataOnUnmount) {
                return () => {
                    onChange("");
                };
            }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally empty: this is an unmount-only cleanup; adding deps would call onChange("") on every re-render
        }, []);

        const {
            isOpen,
            getMenuProps,
            getInputProps,
            getComboboxProps,
            highlightedIndex,
            getItemProps,
        } = useCombobox({
            inputValue: filterValue,
            items: filteredOptions,
            itemToString: (item) => (item ? String(item.label) : ""),
            selectedItem,
            onInputValueChange: ({ inputValue }) => {
                setFilterValue(inputValue);
                !inputValue && onChange("");
            },
            onSelectedItemChange: (item) => {
                onChange(item.selectedItem?.value ?? "");
            },
            stateReducer: (state, actionAndChanges) => {
                const { type, changes } = actionAndChanges;

                // ensures that if you leave the field with a text value that doesn't match the selected item's value, the selected item is cleared
                // ex: if you type "Piz" and then choose "Pizza" from the options, "Pizza" becomes the selected item. If you then delete some characters and leave the field with "Piz", "Piz" doesn't match "Pizza" and the selected item is cleared to ensure form data is submitted accurately
                if (type === useCombobox.stateChangeTypes.InputBlur) {
                    return state.inputValue === state.selectedItem?.label
                        ? changes
                        : {
                              ...changes,
                              selectedItem: null,
                          };
                }
                return changes;
            },
        });

        return (
            <DropdownWrapper>
                <div {...getComboboxProps()}>
                    <TextInput
                        type="search"
                        {...getInputProps({
                            disabled,
                            ref,
                        })}
                        feedbackType={feedbackType}
                        placeholder={placeholder}
                        showClearIcon={true}
                    />
                </div>
                <DropdownList
                    isOpen={isOpen}
                    options={filteredOptions}
                    optionsAreLoading={itemsAreLoading}
                    selectedValue={selectedItem?.value}
                    highlightedIndex={highlightedIndex}
                    getMenuProps={getMenuProps}
                    getItemProps={getItemProps}
                    filterValue={filterValue}
                    setFilterValue={setFilterValue}
                    internalFilterUIShown={internalFilterUIShown}
                    disabledOptionsList={disabledOptionsList}
                    position={position}
                />
            </DropdownWrapper>
        );
    }
);

TextAutocomplete.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
        })
    ),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
    itemsAreLoading: PropTypes.bool,
    disabled: PropTypes.bool,
    feedbackType: PropTypes.oneOf(Object.keys(FEEDBACK_TYPES)),
    clearDataOnUnmount: PropTypes.bool,
    disabledOptionsList: PropTypes.array,
    position: PropTypes.string,
};

export default TextAutocomplete;
